'use client';

import React, { useState, useCallback } from 'react';
import Slide1LeadCapture from './Slide1LeadCapture';
import Slide2Hook from './Slide2Hook';
import Slide3Science from './Slide3Science';
import Slide4Product from './Slide4Product';
import Slide5Decision from './Slide5Decision';
import Slide6ABusiness from './Slide6ABusiness';
import Slide6BProduct from './Slide6BProduct';
import Slide7APackages from './Slide7APackages';

import Slide9OrderForm from './Slide9OrderForm';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTracking } from '@/lib/useTracking';

export interface LeadData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  leadId?: string;
}

export interface AppState {
  currentSlide: number;
  path: 'A' | 'B' | null;
  leadData: LeadData;
  selectedPackage: string;
  selectedAutoship: string;
  orderSubmitted: boolean;
}

// Slide map:
// Path A: 1(lead) → 2(hook) → 3(sci) → 4(prod) → 5(decision) → 6(biz) → 7(packages) → 8(order) → 9(webinar) → 10(done)
// Path B: 1(lead) → 2(hook) → 3(sci) → 4(prod) → 5(decision) → 6(product pricing) → 7(order) → 8(webinar) → ...

const TOTAL_SLIDES = 10;

export default function PitchDeck() {
  const [state, setState] = useState<AppState>({
    currentSlide: 1,
    path: null,
    leadData: { firstName: '', lastName: '', phone: '', email: '' },
    selectedPackage: 'pkg-premium',
    selectedAutoship: 'auto-2',
    orderSubmitted: false,
  });

  const [orderStarted, setOrderStarted] = useState(false);

  const { trackClick } = useTracking({
    currentSlide: state.currentSlide,
    leadId: state.leadData.leadId,
    leadData: {
      firstName: state.leadData.firstName,
      lastName: state.leadData.lastName,
      email: state.leadData.email,
      phone: state.leadData.phone,
    },
    path: state.path,
    orderStarted,
    orderSubmitted: state.orderSubmitted,
  });

  const updateState = useCallback((updates: Partial<AppState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const goToSlide = useCallback((slide: number) => {
    setState(prev => ({ ...prev, currentSlide: slide }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goNext = useCallback(() => {
    setState(prev => ({ ...prev, currentSlide: Math.min(prev.currentSlide + 1, TOTAL_SLIDES) }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goBack = useCallback(() => {
    setState(prev => {
      // Don't allow going back if order has been submitted
      if (prev.orderSubmitted) return prev;
      return { ...prev, currentSlide: Math.max(prev.currentSlide - 1, 2) };
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const progressPercent = Math.round((state.currentSlide / TOTAL_SLIDES) * 100);

  // Determine if current slide is the order form slide
  const isOrderSlide = () => {
    if (state.path === 'A' && state.currentSlide === 8) return true;
    if (state.path === 'B' && state.currentSlide === 7) return true;
    if (state.currentSlide === 9 || state.currentSlide === 10) return true;
    return false;
  };

  // Determine if current slide is the webinar slide (shown after order)
  const isWebinarSlide = () => {
    if (state.path === 'A' && state.currentSlide === 9) return true;
    if (state.path === 'B' && state.currentSlide === 8) return true;
    return false;
  };

  const showBackButton = state.currentSlide > 2 && !state.orderSubmitted;

  const renderSlide = () => {
    switch (state.currentSlide) {
      case 1:
        return (
          <Slide1LeadCapture
            onNext={(data: LeadData) => {
              updateState({ leadData: data });
              trackClick('lead_captured', 'Lead form submitted');
              goToSlide(2);
            }}
          />
        );
      case 2:
        return <Slide2Hook onNext={() => { trackClick('next_slide', 'Hook → Science'); goToSlide(3); }} />;
      case 3:
        return <Slide3Science onNext={() => { trackClick('next_slide', 'Science → Product'); goToSlide(4); }} />;
      case 4:
        return <Slide4Product onNext={() => { trackClick('next_slide', 'Product → Decision'); goToSlide(5); }} />;
      case 5:
        return (
          <Slide5Decision
            onPathA={() => { trackClick('path_selected', 'Path A — Business'); updateState({ path: 'A' }); goToSlide(6); }}
            onPathB={() => { trackClick('path_selected', 'Path B — Product'); updateState({ path: 'B' }); goToSlide(6); }}
          />
        );
      case 6:
        if (state.path === 'A') {
          return <Slide6ABusiness onNext={() => { trackClick('next_slide', 'Business → Packages'); goToSlide(7); }} />;
        }
        return (
          <Slide6BProduct
            onContinue={() => { trackClick('next_slide', 'Product → Order'); goToSlide(7); }}
            onSwitchToA={() => { trackClick('path_switch', 'Switched to Path A'); updateState({ path: 'A' }); goToSlide(6); }}
          />
        );
      case 7:
        if (state.path === 'A') {
          return (
            <Slide7APackages
              selectedPackage={state.selectedPackage}
              selectedAutoship={state.selectedAutoship}
              onSelectPackage={(pkg) => { trackClick('package_selected', pkg); updateState({ selectedPackage: pkg }); }}
              onSelectAutoship={(auto) => { trackClick('autoship_selected', auto); updateState({ selectedAutoship: auto }); }}
              onNext={() => { trackClick('next_slide', 'Packages → Order Form'); setOrderStarted(true); goToSlide(8); }}
            />
          );
        }
        return (
          <Slide9OrderForm
            leadData={state.leadData}
            path={state.path}
            selectedPackage={state.selectedPackage}
            selectedAutoship={state.selectedAutoship}
            onSelectPackage={(pkg) => { trackClick('package_selected', pkg); updateState({ selectedPackage: pkg }); }}
            onSelectAutoship={(auto) => { trackClick('autoship_selected', auto); updateState({ selectedAutoship: auto }); }}
            onOrderSubmitted={() => { updateState({ orderSubmitted: true }); }}
          />
        );
      case 8:
        if (state.path === 'A') {
          return (
            <Slide9OrderForm
              leadData={state.leadData}
              path={state.path}
              selectedPackage={state.selectedPackage}
              selectedAutoship={state.selectedAutoship}
              onSelectPackage={(pkg) => { trackClick('package_selected', pkg); updateState({ selectedPackage: pkg }); }}
              onSelectAutoship={(auto) => { trackClick('autoship_selected', auto); updateState({ selectedAutoship: auto }); }}
              onOrderSubmitted={() => { updateState({ orderSubmitted: true }); }}
            />
          );
        }
        return (
          <Slide9OrderForm
            leadData={state.leadData}
            path={state.path}
            selectedPackage={state.selectedPackage}
            selectedAutoship={state.selectedAutoship}
            onSelectPackage={(pkg) => { trackClick('package_selected', pkg); updateState({ selectedPackage: pkg }); }}
            onSelectAutoship={(auto) => { trackClick('autoship_selected', auto); updateState({ selectedAutoship: auto }); }}
            onOrderSubmitted={() => { updateState({ orderSubmitted: true }); }}
          />
        );
      case 9:
        if (state.path === 'A') {
          return (
            <Slide9OrderForm
              leadData={state.leadData}
              path={state.path}
              selectedPackage={state.selectedPackage}
              selectedAutoship={state.selectedAutoship}
              onSelectPackage={(pkg) => updateState({ selectedPackage: pkg })}
              onSelectAutoship={(auto) => updateState({ selectedAutoship: auto })}
              onOrderSubmitted={() => updateState({ orderSubmitted: true })}
            />
          );
        }
        return (
          <Slide9OrderForm
            leadData={state.leadData}
            path={state.path}
            selectedPackage={state.selectedPackage}
            selectedAutoship={state.selectedAutoship}
            onSelectPackage={(pkg) => updateState({ selectedPackage: pkg })}
            onSelectAutoship={(auto) => updateState({ selectedAutoship: auto })}
            onOrderSubmitted={() => updateState({ orderSubmitted: true })}
          />
        );
      case 10:
        return (
          <Slide9OrderForm
            leadData={state.leadData}
            path={state.path}
            selectedPackage={state.selectedPackage}
            selectedAutoship={state.selectedAutoship}
            onSelectPackage={(pkg) => updateState({ selectedPackage: pkg })}
            onSelectAutoship={(auto) => updateState({ selectedAutoship: auto })}
            onOrderSubmitted={() => updateState({ orderSubmitted: true })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="h-1 bg-muted w-full">
          <div className="progress-bar-fill h-full" style={{ width: `${progressPercent}%` }} />
        </div>
        {/* Top Nav */}
        <div className="flex items-center justify-between px-6 py-3 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-foreground font-bold text-sm tracking-widest">X39</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
          </div>
          <div />
        </div>
      </div>

      {/* Back Button */}
      {showBackButton && (
        <button
          onClick={goBack}
          className="fixed top-20 left-4 z-40 flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors text-sm px-3 py-2 rounded-lg bg-muted/40 hover:bg-muted/60 backdrop-blur-sm border border-border"
        >
          <ChevronLeft size={16} />
          <span className="hidden sm:inline">Back</span>
        </button>
      )}

      {/* Slide Content */}
      <div className="pt-20 min-h-screen slide-enter" key={`slide-${state.currentSlide}`}>
        {renderSlide()}
      </div>

      {/* Slide Navigation Bar */}
      <div className="sticky bottom-0 z-40 bg-background/90 backdrop-blur-md border-t border-border">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
          {/* Back */}
          {showBackButton ? (
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-4 py-2.5 rounded-xl bg-muted/40 hover:bg-muted/70 border border-border hover:border-primary/40 active:scale-95"
            >
              <ChevronLeft size={18} />
              <span>Back</span>
            </button>
          ) : (
            <div />
          )}

          {/* Slide indicator */}
          <div />

          {/* Next — only show on slides where the CTA isn't the primary action */}
          {state.currentSlide > 1 && state.currentSlide < TOTAL_SLIDES &&
           !isOrderSlide() &&
           !(state.path === 'B' && state.currentSlide >= 7) ? (
            <button
              onClick={goNext}
              className="flex items-center gap-2 text-sm font-semibold text-white transition-all px-5 py-2.5 rounded-xl bg-primary hover:bg-primary/90 active:scale-95 shadow-lg shadow-primary/30"
            >
              <span>Next</span>
              <ChevronRight size={18} />
            </button>
          ) : (
            <div />
          )}
        </div>
      </div>

    </div>
  );
}